import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { API_URL, BROKEN_IMAGE_PLACEHOLDER, getImageUrl } from '../config';
import './HomeSlider.css';

const HomeSlider = () => {
  const [slides, setSlides] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitionEnabled, setIsTransitionEnabled] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const desktopTrackRef = useRef(null);
  const mobileTrackRef = useRef(null);
  const activeIndexRef = useRef(0);

  const isMobileViewport = useCallback(
    () => window.matchMedia('(max-width: 768px)').matches,
    []
  );

  const hasMultipleSlides = slides.length > 1;
  const loopedSlides = useMemo(() => {
    if (!hasMultipleSlides) {
      return slides;
    }

    return [slides[slides.length - 1], ...slides, slides[0]];
  }, [hasMultipleSlides, slides]);

  const currentSlide = hasMultipleSlides
    ? (activeIndex - 1 + slides.length) % slides.length
    : 0;

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  const syncTrackPositions = useCallback(() => {
    const updateTrackPosition = (track, cardSelector) => {
      if (!track) return;

      const card = track.querySelector(cardSelector);
      if (!card) return;

      const slideWidth = card.offsetWidth;
      if (slideWidth <= 0) return;

      const trackStyles = window.getComputedStyle(track);
      const gap = parseFloat(trackStyles.columnGap || trackStyles.gap || 0) || 0;
      const index = activeIndexRef.current;

      track.style.transform = `translateX(-${index * (slideWidth + gap)}px)`;
    };

    // Only move the visible track. Updating a hidden track still runs CSS transitions
    // and can fire extra transitionend events that break the infinite loop on desktop.
    if (isMobileViewport()) {
      updateTrackPosition(mobileTrackRef.current, '.slide-card');
    } else {
      updateTrackPosition(desktopTrackRef.current, '.desktop-slide-card');
    }
  }, [isMobileViewport]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const bRes = await axios.get(`${API_URL}/ProductBanners`).catch(() => ({ data: [] }));
        if (cancelled) return;

        const raw = bRes.data;
        const banners = Array.isArray(raw) ? raw : (raw?.$values ?? []);

        const imageSlides = banners
          .map((s, i) => ({
            id: `b-${s.productBannerId ?? s.ProductBannerId ?? i}`,
            image: getImageUrl(s.imageUrl ?? s.ImageUrl),
            link: `/product/${s.productId ?? s.ProductId}`,
          }))
          .filter((slide) => Boolean(slide.image));

        if (cancelled) return;

        setSlides(imageSlides);
        setActiveIndex(imageSlides.length > 1 ? 1 : 0);
        setIsLoading(false);
      } catch {
        if (!cancelled) {
          setSlides([]);
          setActiveIndex(0);
          setIsLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setIsTransitionEnabled(false);
    setActiveIndex(slides.length > 1 ? 1 : 0);
  }, [slides.length]);

  useLayoutEffect(() => {
    syncTrackPositions();
  }, [activeIndex, isTransitionEnabled, loopedSlides.length, syncTrackPositions]);

  useEffect(() => {
    window.addEventListener('resize', syncTrackPositions);
    return () => window.removeEventListener('resize', syncTrackPositions);
  }, [syncTrackPositions]);

  const handleBannerImageLoad = useCallback(() => {
    syncTrackPositions();
  }, [syncTrackPositions]);

  useLayoutEffect(() => {
    if (isTransitionEnabled) return undefined;

    let cancelled = false;
    let outerFrame = 0;
    let innerFrame = 0;

    outerFrame = window.requestAnimationFrame(() => {
      if (cancelled) return;
      syncTrackPositions();
      innerFrame = window.requestAnimationFrame(() => {
        if (cancelled) return;
        setIsTransitionEnabled(true);
      });
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(outerFrame);
      window.cancelAnimationFrame(innerFrame);
    };
  }, [isTransitionEnabled, syncTrackPositions]);

  const nextSlide = useCallback(() => {
    if (!slides.length) return;

    if (!hasMultipleSlides) {
      setActiveIndex(0);
      return;
    }

    if (activeIndexRef.current >= slides.length + 1) return;

    setIsTransitionEnabled(true);
    setActiveIndex((prev) => prev + 1);
  }, [hasMultipleSlides, slides.length]);

  const prevSlide = useCallback(() => {
    if (!slides.length) return;

    if (!hasMultipleSlides) {
      setActiveIndex(0);
      return;
    }

    if (activeIndexRef.current <= 0) return;

    setIsTransitionEnabled(true);
    setActiveIndex((prev) => prev - 1);
  }, [hasMultipleSlides, slides.length]);

  const goToSlide = (index) => {
    if (index === currentSlide || !slides.length) return;

    if (!hasMultipleSlides) {
      setActiveIndex(0);
      return;
    }

    setIsTransitionEnabled(true);
    setActiveIndex(index + 1);
  };

  useEffect(() => {
    if (!slides.length) return;
    const timer = setInterval(nextSlide, 4200);
    return () => clearInterval(timer);
  }, [nextSlide, slides.length]);

  const minSwipeDistance = 50;
  const onTouchStart = (e) => setTouchStart(e.targetTouches[0].clientX);
  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const onTouchEnd = () => {
    if (!touchStart || touchEnd === null) return;
    const distance = touchStart - touchEnd;
    if (Math.abs(distance) > minSwipeDistance) {
      distance > 0 ? nextSlide() : prevSlide();
    }
    setTouchStart(null);
    setTouchEnd(null);
  };

  const handleTransitionEnd = useCallback((event) => {
    if (event.propertyName !== 'transform') return;
    if (!hasMultipleSlides) return;

    const isMobile = isMobileViewport();
    const authoritativeTrack = isMobile ? mobileTrackRef.current : desktopTrackRef.current;
    if (event.currentTarget !== authoritativeTrack) return;

    const index = activeIndexRef.current;

    if (index === 0) {
      const newIndex = slides.length;
      activeIndexRef.current = newIndex;
      setIsTransitionEnabled(false);
      setActiveIndex(newIndex);
      return;
    }

    if (index >= slides.length + 1) {
      activeIndexRef.current = 1;
      setIsTransitionEnabled(false);
      setActiveIndex(1);
    }
  }, [hasMultipleSlides, isMobileViewport, slides.length]);

  if (isLoading) {
    return (
      <div className="home-slider home-slider-loading" aria-hidden="true">
        <div className="slider-loading-placeholder" />
      </div>
    );
  }

  if (!slides.length) {
    return null;
  }

  return (
    <div className="home-slider">
      <div className="slider-container slider-desktop">
        <div
          ref={desktopTrackRef}
          className={`desktop-slider-track ${isTransitionEnabled ? '' : 'no-transition'}`}
          onTransitionEnd={handleTransitionEnd}
        >
          {loopedSlides.map((slide, index) => {
            const slideIndex = hasMultipleSlides
              ? (index - 1 + slides.length) % slides.length
              : index;

            return (
              <div
                key={`${slide.id}-${index}`}
                className={`desktop-slide-card ${slideIndex === currentSlide ? 'is-active' : ''}`}
              >
                <div className="banner-media desktop-banner-media">
                  <img
                    src={slide.image}
                    alt=""
                    className="banner-image desktop-banner-image"
                    onLoad={handleBannerImageLoad}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = BROKEN_IMAGE_PLACEHOLDER;
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div
        className="slider-container slider-mobile"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div
          ref={mobileTrackRef}
          className={`slider-track ${isTransitionEnabled ? '' : 'no-transition'}`}
          onTransitionEnd={handleTransitionEnd}
        >
          {loopedSlides.map((slide, index) => {
            const slideIndex = hasMultipleSlides
              ? (index - 1 + slides.length) % slides.length
              : index;

            return (
              <div
                key={`${slide.id}-${index}`}
                className={`slide-card ${slideIndex === currentSlide ? 'is-active' : ''}`}
              >
                <div className="banner-media mobile-banner-media">
                  <img
                    src={slide.image}
                    alt=""
                    className="banner-image mobile-banner-image"
                    onLoad={handleBannerImageLoad}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = BROKEN_IMAGE_PLACEHOLDER;
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <button className="slider-arrow prev" onClick={prevSlide} aria-label="Previous slide">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 18l-6-6 6-6"/>
        </svg>
      </button>
      <button className="slider-arrow next" onClick={nextSlide} aria-label="Next slide">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 18l6-6-6-6"/>
        </svg>
      </button>

      <div className="slider-dots">
        {slides.map((_, index) => (
          <button
            key={index}
            type="button"
            className={`dot ${index === currentSlide ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default HomeSlider;
